import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheckDouble } from '@fortawesome/pro-regular-svg-icons'

import GlobalStyles from '../../../components/component-items/styles'
import { notify, useFetch } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'
import StrategySimulator from './strategy-simulator'

export type FulfillmentPolicyDraft = {
  id: number
  name: string
  allowPartial: boolean
  allowLinePartial: boolean
  shortageBehavior: 'BACKORDER' | 'CANCEL'
  lineShortageBehavior: 'BACKORDER' | 'CANCEL'
  selectionStrategy: 'PRIMARY_SITE_ONLY' | 'BEST_SINGLE_SITE' | 'BEST_MULTI_SITE'
  updatedAt: string
  description?: string
  warehousesAcceptingOrders: string[]
  isDefault: boolean
}

type WarehouseOption = { value: string; label: string }

const FULFILLMENT_POLICIES_ENDPOINT = '/api/order/fulfillment-policies/'

type PolicyConfig = Pick<
  FulfillmentPolicyDraft,
  'allowPartial' | 'allowLinePartial' | 'shortageBehavior' | 'lineShortageBehavior' | 'selectionStrategy'
>

type BusinessOptionKey =
  | 'SHIP_PARTIAL_BACKORDER'
  | 'SHIP_COMPLETE'
  | 'SHORT_SHIP_CANCEL'
  | 'FILL_ORDER_CANCEL'
  | 'FILL_LINE_CANCEL'
  | 'CUSTOM'

type BusinessOption = {
  key: BusinessOptionKey
  name: string
  summary: string
  config: PolicyConfig
  description?: string
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  h1 {
    font-size: 2rem;
    color: ${(props) => props.theme.colors.mulledWine};
    margin: 0;
  }

  p {
    margin: 0;
    color: ${(props) => props.theme.colors.gray};
    font-size: 1rem;
  }
`

const Card = styled.div`
  background: ${(props) => props.theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(82, 63, 105, 0.12);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.25rem;
    margin: 0;
    color: ${(props) => props.theme.colors.mulledWine};
  }
`

const CardDescription = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.gray};
  font-size: 0.95rem;
`

const InlineForm = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;

  label {
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(props) => props.theme.colors.gray};
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  input,
  select,
  textarea {
    border: 1px solid ${(props) => props.theme.colors.gallery};
    border-radius: 4px;
    padding: 0.55rem 0.65rem;
    font-size: 0.95rem;
    color: ${(props) => props.theme.colors.mulledWine};
  }

  textarea {
    min-height: 90px;
    resize: vertical;
  }
`

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  grid-column: 1 / -1;
`

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
  justify-content: flex-end;
  align-items: center;
  grid-column: 1 / -1;
`

const BusinessOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`

const BusinessOptionButton = styled.button<{ $selected?: boolean }>`
  border: 1px solid ${(props) => (props.$selected ? props.theme.colors.java : props.theme.colors.gallery)};
  border-radius: 8px;
  padding: 1rem;
  text-align: left;
  background: ${(props) => (props.$selected ? 'rgba(29, 201, 183, 0.12)' : props.theme.colors.white)};
  color: ${(props) => props.theme.colors.mulledWine};
  box-shadow: ${(props) =>
    props.$selected ? '0 12px 30px rgba(82, 63, 105, 0.18)' : '0 8px 20px rgba(82, 63, 105, 0.12)'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;

  strong {
    font-size: 1rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${(props) => props.theme.colors.gray};
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.java};
    box-shadow: 0 14px 32px rgba(82, 63, 105, 0.2);
  }
`

const PresetHint = styled.p`
  margin: 0;
  margin-top: 0.75rem;
  font-size: 1rem;
  color: ${(props) => props.theme.colors.gray};
`

const PoliciesTable = styled(GlobalStyles.DataTable)`
  width: 100%;

  thead th {
    padding: 0.75rem 0.5rem;
    text-align: center;

    &:first-child {
      text-align: left;
    }
  }

  tbody tr td {
    padding: 0.75rem 0.5rem;
    text-align: center;
    vertical-align: middle;

    &:first-child {
      text-align: left;
    }
  }

  tbody tr td.actions {
    text-align: center;
    white-space: nowrap;
  }
`

const PolicyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  .policy-name {
    font-weight: 600;
    color: ${(props) => props.theme.colors.mulledWine};
  }

  .policy-description {
    margin: 0;
    color: ${(props) => props.theme.colors.gray};
    font-size: 0.875rem;
  }
`

const CompactStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  text-align: center;
`

const BooleanBadge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(props) => (props.$active ? props.theme.colors.java : props.theme.colors.gray)};
  background-color: ${(props) => (props.$active ? 'rgba(29, 201, 183, 0.12)' : props.theme.colors.whiteLilac)};
`

const Pill = styled.span<{ $variant?: 'info' | 'danger' | 'primary' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  min-width: 2.5rem;
  text-transform: none;
  text-align: center;
  color: ${({ theme, $variant }) => {
    if ($variant === 'danger') return theme.colors.crimson
    if ($variant === 'primary') return theme.colors.white
    if ($variant === 'info') return theme.colors.java
    return theme.colors.gray
  }};
  background-color: ${({ theme, $variant }) => {
    if ($variant === 'danger') return 'rgba(227, 20, 20, 0.12)'
    if ($variant === 'primary') return '#800080'
    if ($variant === 'info') return 'rgba(29, 201, 183, 0.12)'
    return theme.colors.whiteLilac
  }};
`

const SetDefaultButton = styled.button`
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 4px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  background: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.gray};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.java};
    color: ${(props) => props.theme.colors.java};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const InlineInput = styled.input`
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  width: 100%;
`

const InlineSelect = styled.select`
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  width: 100%;
`

const EmptyState = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: ${(props) => props.theme.colors.gray};
  font-size: 1rem;
`

const InlineSelectWrapper = styled.div`
  .policy-warehouse-select__control {
    border: 1px solid ${(props) => props.theme.colors.gallery};
    min-height: 2.25rem;
    box-shadow: none;
  }

  .policy-warehouse-select__control--is-focused {
    border-color: ${(props) => props.theme.colors.java};
    box-shadow: 0 0 0 1px ${(props) => props.theme.colors.java};
  }

  .policy-warehouse-select__multi-value {
    background-color: ${(props) => props.theme.colors.whiteLilac};
  }

  font-size: 0.875rem;
`

const defaultDraft: FulfillmentPolicyDraft = {
  id: 0,
  name: '',
  allowPartial: true,
  allowLinePartial: false,
  shortageBehavior: 'BACKORDER',
  lineShortageBehavior: 'BACKORDER',
  selectionStrategy: 'PRIMARY_SITE_ONLY',
  updatedAt: new Date().toISOString(),
  description: '',
  warehousesAcceptingOrders: [],
  isDefault: false,
}

const parseJsonSafely = async (response: Response) => {
  try {
    return await response.json()
  } catch (error) {
    return null
  }
}

const normalizeWarehouseIds = (rawValue: any): string[] => {
  if (!rawValue) {
    return []
  }

  const list = Array.isArray(rawValue) ? rawValue : []

  return list
    .map((entry) => {
      if (typeof entry === 'string' || typeof entry === 'number') {
        return String(entry)
      }

      if (entry && typeof entry === 'object') {
        if ('id' in entry && entry.id !== undefined) {
          return String(entry.id)
        }

        if ('value' in entry && entry.value !== undefined) {
          return String(entry.value)
        }
      }

      return null
    })
    .filter((value): value is string => Boolean(value))
}

const normalizePolicy = (policy: any): FulfillmentPolicyDraft => ({
  id: Number(policy?.id ?? 0),
  name: policy?.name ?? '',
  allowPartial: Boolean(policy?.allowPartial ?? defaultDraft.allowPartial),
  allowLinePartial: Boolean(policy?.allowLinePartial ?? defaultDraft.allowLinePartial),
  shortageBehavior: (policy?.shortageBehavior ??
    defaultDraft.shortageBehavior) as FulfillmentPolicyDraft['shortageBehavior'],
  lineShortageBehavior: (policy?.lineShortageBehavior ??
    defaultDraft.lineShortageBehavior) as FulfillmentPolicyDraft['lineShortageBehavior'],
  selectionStrategy: (policy?.selectionStrategy ??
    defaultDraft.selectionStrategy) as FulfillmentPolicyDraft['selectionStrategy'],
  updatedAt: policy?.updatedAt ?? new Date().toISOString(),
  description: policy?.description ?? '',
  warehousesAcceptingOrders: normalizeWarehouseIds(policy?.warehousesAcceptingOrders),
  isDefault: Boolean(policy?.isDefault ?? false),
})

const serializePolicyInput = (policy: FulfillmentPolicyDraft) => ({
  name: policy.name,
  allowPartial: policy.allowPartial,
  allowLinePartial: policy.allowLinePartial,
  shortageBehavior: policy.shortageBehavior,
  lineShortageBehavior: policy.lineShortageBehavior,
  selectionStrategy: policy.selectionStrategy,
  description: policy.description,
  warehousesAcceptingOrders: policy.warehousesAcceptingOrders,
  isDefault: policy.isDefault,
})

type ValidationError = {
  field: string
  message: string
}

const validatePolicy = (policy: FulfillmentPolicyDraft): ValidationError[] => {
  const errors: ValidationError[] = []
  const warehouseCount = policy.warehousesAcceptingOrders.length

  // allowLinePartial requires allowPartial
  if (policy.allowLinePartial && !policy.allowPartial) {
    errors.push({
      field: 'allowLinePartial',
      message: 'Allow Line Partials requires Allow Partial Handling Units to be enabled.',
    })
  }

  // BEST_MULTI_SITE requires allowPartial
  if (policy.selectionStrategy === 'BEST_MULTI_SITE' && !policy.allowPartial) {
    errors.push({
      field: 'selectionStrategy',
      message: 'Best Multi Site strategy requires Allow Partial Handling Units to be enabled.',
    })
  }

  // BEST_MULTI_SITE requires at least 2 warehouses
  if (policy.selectionStrategy === 'BEST_MULTI_SITE' && warehouseCount < 2) {
    errors.push({
      field: 'selectionStrategy',
      message: 'Best Multi Site strategy requires at least 2 warehouses.',
    })
  }

  // BEST_SINGLE_SITE requires at least 2 warehouses
  if (policy.selectionStrategy === 'BEST_SINGLE_SITE' && warehouseCount < 2) {
    errors.push({
      field: 'selectionStrategy',
      message: 'Best Single Site strategy requires at least 2 warehouses.',
    })
  }

  return errors
}

const BUSINESS_OPTIONS: BusinessOption[] = [
  {
    key: 'SHIP_COMPLETE',
    name: 'Ship Complete',
    summary: 'Wait until one warehouse can cover every line before releasing the order.',
    config: {
      allowPartial: false,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'BACKORDER',
      selectionStrategy: 'PRIMARY_SITE_ONLY',
    },
    description: 'Keep the order intact and avoid partials until the selected site has full inventory.',
  },
  {
    key: 'FILL_ORDER_CANCEL',
    name: 'Fill Entire Order or Cancel Entire Order',
    summary: 'If no site can cover everything, cancel the order instead of backordering.',
    config: {
      allowPartial: false,
      allowLinePartial: false,
      shortageBehavior: 'CANCEL',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'PRIMARY_SITE_ONLY',
    },
    description: 'Ensure customers either receive the full order or a cancellation notice.',
  },
  {
    key: 'SHIP_PARTIAL_BACKORDER',
    name: 'Ship Partial and Backorder Remaining',
    summary: 'Release what is ready today and keep the remaining demand on backorder.',
    config: {
      allowPartial: true,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'BACKORDER',
      selectionStrategy: 'BEST_MULTI_SITE',
    },
    description: 'Allow multi-site fulfillment while holding any shortages on backorder for later release.',
  },
  {
    key: 'SHORT_SHIP_CANCEL',
    name: 'Short Ship and Cancel Remaining',
    summary: 'Ship the available quantity and cancel any remaining shortage.',
    config: {
      allowPartial: true,
      allowLinePartial: true,
      shortageBehavior: 'CANCEL',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'BEST_MULTI_SITE',
    },
    description: 'Permit partial releases across sites but cancel whatever cannot ship after planning.',
  },
  {
    key: 'FILL_LINE_CANCEL',
    name: 'Fill Entire Line or Cancel Entire Line',
    summary: 'Keep each SKU all-or-nothing while allowing other lines to ship.',
    config: {
      allowPartial: true,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'BEST_SINGLE_SITE',
    },
    description: 'Allow the order to split across runs, but cancel any lines that cannot be fulfilled in full.',
  },
  {
    key: 'CUSTOM',
    name: 'Custom configuration',
    summary: 'Start from scratch and fine-tune every rule yourself.',
    config: {
      allowPartial: defaultDraft.allowPartial,
      allowLinePartial: defaultDraft.allowLinePartial,
      shortageBehavior: defaultDraft.shortageBehavior,
      lineShortageBehavior: defaultDraft.lineShortageBehavior,
      selectionStrategy: defaultDraft.selectionStrategy,
    },
  },
]

const resolveOptionKeyFromDraft = (policy: FulfillmentPolicyDraft): BusinessOptionKey => {
  const match = BUSINESS_OPTIONS.find(
    (option) =>
      option.key !== 'CUSTOM' &&
      policy.allowPartial === option.config.allowPartial &&
      policy.shortageBehavior === option.config.shortageBehavior &&
      policy.allowLinePartial === option.config.allowLinePartial &&
      policy.lineShortageBehavior === option.config.lineShortageBehavior &&
      policy.selectionStrategy === option.config.selectionStrategy
  )

  return match ? match.key : 'CUSTOM'
}

export const FulfillmentPoliciesPage = () => {
  const [policies, setPolicies] = useState<FulfillmentPolicyDraft[]>([])
  const [draft, setDraft] = useState<FulfillmentPolicyDraft>(defaultDraft)
  const [editingPolicyId, setEditingPolicyId] = useState<number | null>(null)
  const [editingDraft, setEditingDraft] = useState<FulfillmentPolicyDraft | null>(null)
  const [selectedOption, setSelectedOption] = useState<BusinessOptionKey | null>(null)
  const [showConfiguration, setShowConfiguration] = useState(false)
  const [isSubmittingNewPolicy, setIsSubmittingNewPolicy] = useState(false)
  const [savingPolicyId, setSavingPolicyId] = useState<number | null>(null)
  const [deletingPolicyId, setDeletingPolicyId] = useState<number | null>(null)
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null)

  const {
    state: { activeWarehouses: activeWarehousesWithID, csrf },
  } = globalState()

  const {
    response: policiesResponse,
    loaded: policiesLoaded,
    error: policiesError,
  } = useFetch(FULFILLMENT_POLICIES_ENDPOINT, {})

  useEffect(() => {
    if (!policiesLoaded) {
      return
    }

    const rawPolicies = Array.isArray(policiesResponse?.results) ? policiesResponse.results : []
    setPolicies(rawPolicies.map(normalizePolicy))
  }, [policiesLoaded, policiesResponse])

  useEffect(() => {
    if (policiesError) {
      notify({ title: 'Error!', message: 'Unable to load fulfillment policies.', type: 'error' })
    }
  }, [policiesError])

  const warehouseOptions = useMemo<WarehouseOption[]>(
    () =>
      (activeWarehousesWithID || []).map((warehouse: any) => ({
        value: String(warehouse.id),
        label: warehouse.name,
      })),
    [activeWarehousesWithID]
  )

  const warehouseLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    warehouseOptions.forEach((option) => {
      map.set(option.value, option.label)
    })
    return map
  }, [warehouseOptions])

  const sendPolicyRequest = async (
    method: 'POST' | 'PATCH' | 'DELETE',
    payload?: Partial<ReturnType<typeof serializePolicyInput>>,
    policyId?: number
  ) => {
    const endpoint = policyId ? `${FULFILLMENT_POLICIES_ENDPOINT}${policyId}/` : FULFILLMENT_POLICIES_ENDPOINT

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrf || '',
      },
      body: payload ? JSON.stringify(payload) : undefined,
    })

    if (!response.ok) {
      const errorPayload = await parseJsonSafely(response)
      const message =
        (errorPayload && (errorPayload.detail || errorPayload.error)) || `Request failed with status ${response.status}`
      throw new Error(message)
    }

    if (response.status === 204) {
      return null
    }

    return parseJsonSafely(response)
  }

  const updateDraft = (patch: Partial<FulfillmentPolicyDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch }

      // Auto-reset logic when allowPartial changes to false
      if ('allowPartial' in patch && patch.allowPartial === false) {
        // Reset allowLinePartial since it requires allowPartial
        next.allowLinePartial = false
        // Reset lineShortageBehavior to default
        next.lineShortageBehavior = 'BACKORDER'
        // Reset strategy if it was BEST_MULTI_SITE (requires allowPartial)
        if (next.selectionStrategy === 'BEST_MULTI_SITE') {
          next.selectionStrategy = 'PRIMARY_SITE_ONLY'
        }
      }

      // Auto-reset when allowLinePartial changes to false
      if ('allowLinePartial' in patch && patch.allowLinePartial === false) {
        next.lineShortageBehavior = 'BACKORDER'
      }

      // Auto-reset when warehouses drop below 2
      if ('warehousesAcceptingOrders' in patch) {
        const warehouseCount = (patch.warehousesAcceptingOrders || []).length
        if (warehouseCount < 2) {
          if (next.selectionStrategy === 'BEST_MULTI_SITE' || next.selectionStrategy === 'BEST_SINGLE_SITE') {
            next.selectionStrategy = 'PRIMARY_SITE_ONLY'
          }
        }
      }

      // Only recalculate selected option if configuration fields changed (not warehouses, name, or description)
      if (showConfiguration) {
        const configFieldsChanged = Object.keys(patch).some(
          (key) => key !== 'warehousesAcceptingOrders' && key !== 'name' && key !== 'description'
        )
        if (configFieldsChanged) {
          setSelectedOption(resolveOptionKeyFromDraft(next))
        }
      }

      return next
    })
  }

  const updateEditingDraft = (patch: Partial<FulfillmentPolicyDraft>) => {
    setEditingDraft((prev) => {
      if (!prev) return prev

      const next = { ...prev, ...patch }

      // Auto-reset logic when allowPartial changes to false
      if ('allowPartial' in patch && patch.allowPartial === false) {
        // Reset allowLinePartial since it requires allowPartial
        next.allowLinePartial = false
        // Reset lineShortageBehavior to default
        next.lineShortageBehavior = 'BACKORDER'
        // Reset strategy if it was BEST_MULTI_SITE (requires allowPartial)
        if (next.selectionStrategy === 'BEST_MULTI_SITE') {
          next.selectionStrategy = 'PRIMARY_SITE_ONLY'
        }
      }

      // Auto-reset when allowLinePartial changes to false
      if ('allowLinePartial' in patch && patch.allowLinePartial === false) {
        next.lineShortageBehavior = 'BACKORDER'
      }

      // Auto-reset when warehouses drop below 2
      if ('warehousesAcceptingOrders' in patch) {
        const warehouseCount = (patch.warehousesAcceptingOrders || []).length
        if (warehouseCount < 2) {
          if (next.selectionStrategy === 'BEST_MULTI_SITE' || next.selectionStrategy === 'BEST_SINGLE_SITE') {
            next.selectionStrategy = 'PRIMARY_SITE_ONLY'
          }
        }
      }

      return next
    })
  }

  const activeOptionKey = useMemo(() => {
    if (showConfiguration) {
      return selectedOption ?? resolveOptionKeyFromDraft(draft)
    }

    return selectedOption
  }, [draft, selectedOption, showConfiguration])

  const handleBusinessOptionClick = (option: BusinessOption) => {
    setShowConfiguration(true)
    setDraft((prev) => {
      const next: FulfillmentPolicyDraft = {
        ...prev,
        allowPartial: option.config.allowPartial,
        shortageBehavior: option.config.shortageBehavior,
        allowLinePartial: option.config.allowLinePartial,
        lineShortageBehavior: option.config.lineShortageBehavior,
        selectionStrategy: option.config.selectionStrategy,
      }

      if (option.key !== 'CUSTOM') {
        const shouldReplaceName = !prev.name || selectedOption !== option.key
        next.name = shouldReplaceName ? option.name : prev.name
        next.description = option.description ?? prev.description
      } else {
        next.name = prev.name
        next.description = prev.description
      }

      return next
    })
    setSelectedOption(option.key)
  }

  const resetDraft = () => {
    setDraft({ ...defaultDraft, id: 0, updatedAt: new Date().toISOString() })
    setSelectedOption(null)
    setShowConfiguration(false)
  }

  const handleCreatePolicy = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!draft.name.trim() || isSubmittingNewPolicy) {
      return
    }

    // Validate policy before saving
    const validationErrors = validatePolicy(draft)
    if (validationErrors.length > 0) {
      notify({
        title: 'Validation Error',
        message: validationErrors.map((e) => e.message).join(' '),
        type: 'error',
      })
      return
    }

    setIsSubmittingNewPolicy(true)

    try {
      const payload = serializePolicyInput(draft)
      const response = await sendPolicyRequest('POST', payload)
      if (response) {
        const createdPolicy = normalizePolicy(response)
        setPolicies((prev) => [...prev, createdPolicy])
      }
      resetDraft()
    } catch (error: any) {
      notify({ title: 'Error!', message: error?.message || 'Unable to create policy.', type: 'error' })
    } finally {
      setIsSubmittingNewPolicy(false)
    }
  }

  const startEditing = (policy: FulfillmentPolicyDraft) => {
    setEditingPolicyId(policy.id)
    setEditingDraft({ ...policy })
  }

  const cancelEditing = () => {
    setEditingPolicyId(null)
    setEditingDraft(null)
  }

  const saveEditing = async () => {
    if (!editingDraft || savingPolicyId === editingDraft.id) {
      return
    }

    // Validate policy before saving
    const validationErrors = validatePolicy(editingDraft)
    if (validationErrors.length > 0) {
      notify({
        title: 'Validation Error',
        message: validationErrors.map((e) => e.message).join(' '),
        type: 'error',
      })
      return
    }

    setSavingPolicyId(editingDraft.id)

    try {
      const payload = serializePolicyInput(editingDraft)
      const response = await sendPolicyRequest('PATCH', payload, editingDraft.id)
      if (response) {
        const updatedPolicy = normalizePolicy(response)
        setPolicies((prev) => prev.map((policy) => (policy.id === updatedPolicy.id ? updatedPolicy : policy)))
        cancelEditing()
      }
    } catch (error: any) {
      notify({ title: 'Error!', message: error?.message || 'Unable to update policy.', type: 'error' })
    } finally {
      setSavingPolicyId(null)
    }
  }

  const deletePolicy = async (policyId: number) => {
    if (deletingPolicyId === policyId) {
      return
    }

    setDeletingPolicyId(policyId)

    try {
      await sendPolicyRequest('DELETE', undefined, policyId)
      setPolicies((prev) => prev.filter((policy) => policy.id !== policyId))
      if (editingPolicyId === policyId) {
        cancelEditing()
      }
    } catch (error: any) {
      notify({ title: 'Error!', message: error?.message || 'Unable to delete policy.', type: 'error' })
    } finally {
      setDeletingPolicyId(null)
    }
  }

  const setAsDefault = async (policyId: number) => {
    if (settingDefaultId === policyId) {
      return
    }

    setSettingDefaultId(policyId)

    try {
      const response = await sendPolicyRequest('PATCH', { isDefault: true }, policyId)
      if (response) {
        const updatedPolicy = normalizePolicy(response)
        // Update all policies: clear isDefault for others, set for the target
        setPolicies((prev) =>
          prev.map((policy) => ({
            ...policy,
            isDefault: policy.id === updatedPolicy.id ? updatedPolicy.isDefault : false,
          }))
        )
      }
    } catch (error: any) {
      notify({ title: 'Error!', message: error?.message || 'Unable to set default policy.', type: 'error' })
    } finally {
      setSettingDefaultId(null)
    }
  }

  const policyRows = useMemo(() => {
    if (!policies.length) {
      return null
    }

    return policies.map((policy) => {
      const isEditing = editingPolicyId === policy.id
      const rowDraft = isEditing && editingDraft ? editingDraft : policy
      const isSaving = savingPolicyId === policy.id
      const isDeleting = deletingPolicyId === policy.id

      return (
        <tr key={policy.id}>
          <td>
            {isEditing ? (
              <PolicyDetails>
                <InlineInput
                  value={rowDraft.name}
                  onChange={(event) =>
                    setEditingDraft({
                      ...(rowDraft as FulfillmentPolicyDraft),
                      name: event.target.value,
                    })
                  }
                />
                <textarea
                  value={rowDraft.description || ''}
                  onChange={(event) =>
                    setEditingDraft({
                      ...(rowDraft as FulfillmentPolicyDraft),
                      description: event.target.value,
                    })
                  }
                  placeholder="Describe when to use this policy"
                />
              </PolicyDetails>
            ) : (
              <PolicyDetails>
                <span className="policy-name">{policy.name}</span>
                {policy.description ? (
                  <p className="policy-description">{policy.description}</p>
                ) : (
                  <p className="policy-description">No description provided.</p>
                )}
              </PolicyDetails>
            )}
          </td>
          <td>
            {isEditing ? (
              <CompactStack>
                <InlineSelect
                  value={rowDraft.shortageBehavior}
                  onChange={(event) =>
                    updateEditingDraft({
                      shortageBehavior: event.target.value as FulfillmentPolicyDraft['shortageBehavior'],
                    })
                  }
                >
                  <option value="BACKORDER">Backorder Remaining</option>
                  <option value="CANCEL">Cancel Remaining</option>
                </InlineSelect>
                {rowDraft.allowLinePartial && (
                  <InlineSelect
                    value={rowDraft.lineShortageBehavior}
                    onChange={(event) =>
                      updateEditingDraft({
                        lineShortageBehavior: event.target.value as FulfillmentPolicyDraft['lineShortageBehavior'],
                      })
                    }
                  >
                    <option value="BACKORDER">Backorder Line</option>
                    <option value="CANCEL">Cancel Line</option>
                  </InlineSelect>
                )}
              </CompactStack>
            ) : (
              <CompactStack>
                <Pill $variant="neutral">
                  {policy.shortageBehavior === 'BACKORDER' ? 'Backorder Remaining' : 'Cancel Remaining'}
                </Pill>
                {policy.allowLinePartial && (
                  <Pill $variant="neutral">
                    {policy.lineShortageBehavior === 'BACKORDER' ? 'Backorder Line' : 'Cancel Line'}
                  </Pill>
                )}
              </CompactStack>
            )}
          </td>
          <td>
            {isEditing ? (
              <InlineSelect
                value={rowDraft.selectionStrategy}
                onChange={(event) =>
                  updateEditingDraft({
                    selectionStrategy: event.target.value as FulfillmentPolicyDraft['selectionStrategy'],
                  })
                }
              >
                <option value="PRIMARY_SITE_ONLY">Primary Site Only</option>
                {rowDraft.warehousesAcceptingOrders.length >= 2 && (
                  <option value="BEST_SINGLE_SITE">Best Single Site</option>
                )}
                {rowDraft.warehousesAcceptingOrders.length >= 2 && rowDraft.allowPartial && (
                  <option value="BEST_MULTI_SITE">Best Multi Site</option>
                )}
              </InlineSelect>
            ) : (
              <Pill $variant="primary">{policy.selectionStrategy.replace(/_/g, ' ').toLowerCase()}</Pill>
            )}
          </td>
          <td>
            {isEditing ? (
              <CompactStack>
                <InlineSelect
                  value={rowDraft.allowPartial ? 'yes' : 'no'}
                  onChange={(event) =>
                    updateEditingDraft({
                      allowPartial: event.target.value === 'yes',
                    })
                  }
                >
                  <option value="yes">Allow Partials</option>
                  <option value="no">No Partials</option>
                </InlineSelect>
                {rowDraft.allowPartial && (
                  <InlineSelect
                    value={rowDraft.allowLinePartial ? 'yes' : 'no'}
                    onChange={(event) =>
                      updateEditingDraft({
                        allowLinePartial: event.target.value === 'yes',
                      })
                    }
                  >
                    <option value="yes">Allow Line Partials</option>
                    <option value="no">No Line Partials</option>
                  </InlineSelect>
                )}
              </CompactStack>
            ) : (
              <CompactStack>
                {policy.allowPartial || policy.allowLinePartial ? (
                  <>
                    {policy.allowPartial && <BooleanBadge $active>Partial</BooleanBadge>}
                    {policy.allowLinePartial && <BooleanBadge $active>Line Partials</BooleanBadge>}
                  </>
                ) : (
                  <FontAwesomeIcon icon={faTimes} style={{ color: '#888' }} />
                )}
              </CompactStack>
            )}
          </td>
          <td>
            {isEditing ? (
              <InlineSelectWrapper>
                <Select
                  isMulti
                  classNamePrefix="policy-warehouse-select"
                  options={warehouseOptions}
                  value={warehouseOptions.filter((option) =>
                    (rowDraft.warehousesAcceptingOrders || []).includes(option.value)
                  )}
                  onChange={(selected: WarehouseOption[] | null) =>
                    updateEditingDraft({
                      warehousesAcceptingOrders: (selected || []).map((option: WarehouseOption) => option.value),
                    })
                  }
                />
              </InlineSelectWrapper>
            ) : (
              <span className="text">
                {policy.warehousesAcceptingOrders.length
                  ? policy.warehousesAcceptingOrders.map((id) => warehouseLabelMap.get(id) ?? id).join(', ')
                  : '—'}
              </span>
            )}
          </td>
          <td>
            <span className="text">{new Date(policy.updatedAt).toLocaleString()}</span>
          </td>
          <td>
            {policy.isDefault ? (
              <FontAwesomeIcon icon={faCheckDouble} style={{ color: 'green', fontSize: '1.25rem' }} />
            ) : (
              <SetDefaultButton
                type="button"
                onClick={() => setAsDefault(policy.id)}
                disabled={settingDefaultId === policy.id}
              >
                {settingDefaultId === policy.id ? 'Setting...' : 'Set as Default'}
              </SetDefaultButton>
            )}
          </td>
          <td className="actions">
            {isEditing ? (
              <ActionsRow>
                <GlobalStyles.Button className="secondary" type="button" onClick={cancelEditing} disabled={isSaving}>
                  Cancel
                </GlobalStyles.Button>
                <GlobalStyles.Button type="button" onClick={saveEditing} disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Save'}
                </GlobalStyles.Button>
              </ActionsRow>
            ) : (
              <ActionsRow>
                <GlobalStyles.Button className="secondary" type="button" onClick={() => startEditing(policy)}>
                  Edit
                </GlobalStyles.Button>
                <GlobalStyles.Button
                  className="red"
                  type="button"
                  onClick={() => deletePolicy(policy.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </GlobalStyles.Button>
              </ActionsRow>
            )}
          </td>
        </tr>
      )
    })
  }, [
    policies,
    editingPolicyId,
    editingDraft,
    warehouseOptions,
    warehouseLabelMap,
    savingPolicyId,
    deletingPolicyId,
    settingDefaultId,
    updateEditingDraft,
  ])

  return (
    <PageWrapper>
      <PageHeader>
        <h1>Fulfillment Policies</h1>
        <p>Create and manage the rules that drive split orders, multi-site routing, and shortage handling.</p>
      </PageHeader>

      <Card>
        <CardHeader>
          <h2>New Policy</h2>
        </CardHeader>
        <CardDescription>
          Select a business option to pre-fill the policy and then fine-tune any details you need.
        </CardDescription>
        <BusinessOptionsGrid>
          {BUSINESS_OPTIONS.map((option) => (
            <BusinessOptionButton
              key={option.key}
              type="button"
              onClick={() => handleBusinessOptionClick(option)}
              $selected={option.key === activeOptionKey}
            >
              <strong>{option.name}</strong>
              <p>{option.summary}</p>
            </BusinessOptionButton>
          ))}
        </BusinessOptionsGrid>
        <PresetHint>
          Choose an option above to load the matching criteria. The configuration form will appear so you can tweak the
          rules or start with a custom setup.
        </PresetHint>
        {showConfiguration ? (
          <InlineForm onSubmit={handleCreatePolicy}>
            <TopRow>
              {/* 1. Name */}
              <label>
                Name
                <input
                  value={draft.name}
                  onChange={(event) => updateDraft({ name: event.target.value })}
                  placeholder="Policy Name"
                />
              </label>
              {/* 2. Warehouses Accepting Orders */}
              <label>
                Warehouses Accepting Orders
                <InlineSelectWrapper>
                  <Select
                    isMulti
                    classNamePrefix="policy-warehouse-select"
                    options={warehouseOptions}
                    value={warehouseOptions.filter((option) => draft.warehousesAcceptingOrders.includes(option.value))}
                    onChange={(selected: WarehouseOption[] | null) =>
                      updateDraft({
                        warehousesAcceptingOrders: (selected || []).map((option: WarehouseOption) => option.value),
                      })
                    }
                  />
                </InlineSelectWrapper>
              </label>
            </TopRow>
            {/* 3. Allow Partial Handling Units */}
            <label>
              Allow Partial Handling Units
              <select
                value={draft.allowPartial ? 'yes' : 'no'}
                onChange={(event) => updateDraft({ allowPartial: event.target.value === 'yes' })}
              >
                <option value="yes">Enabled</option>
                <option value="no">Disabled</option>
              </select>
            </label>
            {/* 4. Allow Line Partials (conditional - only if allowPartial) */}
            {draft.allowPartial && (
              <label>
                Allow Line Partials
                <select
                  value={draft.allowLinePartial ? 'yes' : 'no'}
                  onChange={(event) => updateDraft({ allowLinePartial: event.target.value === 'yes' })}
                >
                  <option value="yes">Enabled</option>
                  <option value="no">Disabled</option>
                </select>
              </label>
            )}
            {/* 5. Selection Strategy (with filtered options) */}
            <label>
              Selection Strategy
              <select
                value={draft.selectionStrategy}
                onChange={(event) =>
                  updateDraft({
                    selectionStrategy: event.target.value as FulfillmentPolicyDraft['selectionStrategy'],
                  })
                }
              >
                <option value="PRIMARY_SITE_ONLY">Primary Site Only</option>
                {draft.warehousesAcceptingOrders.length >= 2 && (
                  <option value="BEST_SINGLE_SITE">Best Single Site</option>
                )}
                {draft.warehousesAcceptingOrders.length >= 2 && draft.allowPartial && (
                  <option value="BEST_MULTI_SITE">Best Multi Site</option>
                )}
              </select>
            </label>
            {/* 6. Shortage Behavior */}
            <label>
              Shortage Behavior
              <select
                value={draft.shortageBehavior}
                onChange={(event) =>
                  updateDraft({
                    shortageBehavior: event.target.value as FulfillmentPolicyDraft['shortageBehavior'],
                  })
                }
              >
                <option value="BACKORDER">Backorder Remaining</option>
                <option value="CANCEL">Cancel Remaining</option>
              </select>
            </label>
            {/* 7. Line Shortage Behavior (conditional - only if allowLinePartial) */}
            {draft.allowLinePartial && (
              <label>
                Line Shortage Behavior
                <select
                  value={draft.lineShortageBehavior}
                  onChange={(event) =>
                    updateDraft({
                      lineShortageBehavior: event.target.value as FulfillmentPolicyDraft['lineShortageBehavior'],
                    })
                  }
                >
                  <option value="BACKORDER">Backorder Line</option>
                  <option value="CANCEL">Cancel Line</option>
                </select>
              </label>
            )}
            {/* 8. Description */}
            <label style={{ gridColumn: '1 / -1' }}>
              Description
              <textarea
                value={draft.description || ''}
                onChange={(event) => updateDraft({ description: event.target.value })}
                placeholder="Summarize how this policy should be used"
              />
            </label>
            <ActionsRow>
              <GlobalStyles.Button
                className="secondary"
                type="button"
                onClick={resetDraft}
                disabled={isSubmittingNewPolicy}
              >
                Reset
              </GlobalStyles.Button>
              <GlobalStyles.Button type="submit" disabled={!draft.name.trim() || isSubmittingNewPolicy}>
                {isSubmittingNewPolicy ? 'Saving…' : 'Add Policy'}
              </GlobalStyles.Button>
            </ActionsRow>
          </InlineForm>
        ) : null}
      </Card>

      <StrategySimulator />

      <Card>
        <CardHeader>
          <h2>Policy Library</h2>
        </CardHeader>
        <CardDescription>Policies in this table are immediately available to split-order planning.</CardDescription>
        {policies.length ? (
          <PoliciesTable>
            <thead>
              <tr>
                <th>
                  <span className="text">Policy</span>
                </th>
                <th>
                  <span className="text">Shortages</span>
                </th>
                <th>
                  <span className="text">Strategy</span>
                </th>
                <th>
                  <span className="text">Partials</span>
                </th>
                <th>
                  <span className="text">Warehouses</span>
                </th>
                <th>
                  <span className="text">Last Updated</span>
                </th>
                <th>
                  <span className="text">Default</span>
                </th>
                <th>
                  <span className="text">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>{policyRows}</tbody>
          </PoliciesTable>
        ) : (
          <EmptyState>No fulfillment policies defined yet. Create one above to get started.</EmptyState>
        )}
      </Card>
    </PageWrapper>
  )
}

export default FulfillmentPoliciesPage
